import cp = require('child_process');
import path = require('path');
import { byteOffsetAt, getBinPath } from './util';
import { getGoVersion, SemVersion, goKeywords, isPositionInString, getToolsEnvVars,
    getFileArchive, Position, Location } from './util';
import Uri from 'vscode-uri';

const missingToolMsg = 'Missing tool: ';

export interface GoDefinitionInformation {
    file: string;
    line: number;
    column: number;
    doc: string;
    declarationlines: string[];
    name: string;
    toolUsed: string;
}

export function definitionLocation(document, position, includeDocs: boolean): Promise<GoDefinitionInformation> {
    let wordRange = document.getWordRangeAtPosition(position);
    let lineText = document.lineAt(position.line).text;
    let word = wordRange ? document.getText(wordRange) : '';
    if (!wordRange || lineText.startsWith('//') || isPositionInString(document, position) || word.match(/^\d+.?\d+$/) || goKeywords.indexOf(word) > 0) {
    return Promise.resolve(null);
    }

    let toolForDocs = 'godoc';
    let offset = byteOffsetAt(document, position);
    let env = getToolsEnvVars();

    return getGoVersion().then((ver: SemVersion) => {
        if (toolForDocs === 'godoc' || (ver && (ver.major < 1 || (ver.major === 1 && ver.minor < 6)))) {
            return definitionLocation_godef(document, position, offset, includeDocs, env);
        }

        return definitionLocation_gogetdoc(document, position, offset, env, true);
    });
}

function definitionLocation_godef(document, position, offset: number, includeDocs: boolean, env: any): Promise<GoDefinitionInformation> {
    let godef = getBinPath('godef');
    if (!path.isAbsolute(godef)) {
        return Promise.reject(missingToolMsg + 'godef');
    }
    let p: cp.ChildProcess;

    return new Promise<GoDefinitionInformation>((resolve, reject) => {
        p = cp.execFile(godef, ['-t', '-i', '-f', document.fileName, '-o', offset.toString()], { env }, (err, stdout, stderr) => {
            try {
                if (err && (<any>err).code === 'ENOENT') {
                    return reject(missingToolMsg + 'godef');
                }
                if (err) {
                    return reject(err.message || stderr);
                }
                let result = stdout.toString();
                let lines = result.split('\n');
                let match = /(.*):(\d+):(\d+)/.exec(lines[0]);
                if (!match) {
                    return resolve(null);
                }
                let [_, file, line, col] = match;
                let signature = lines[1];
                let godoc = getBinPath('godoc');
                let pkgPath = path.dirname(file);
                let definitionInformation: GoDefinitionInformation = {
                    file: file,
                    line: +line - 1,
                    column: + col - 1,
                    declarationlines: lines.splice(1),
                    toolUsed: 'godef',
                    doc: null,
                    name: null,
                };
                if (!includeDocs) {
                    return resolve(definitionInformation);
                }
                p = cp.execFile(godoc, [pkgPath], { env }, (err, stdout, stderr) => {
                    if (err && (<any>err).code === 'ENOENT') {
                        console.log('The "godoc" command is not available.');
                    }
                    let godocLines = stdout.toString().split('\n');
                    let doc = '';
                    let sigName = signature.substring(0, signature.indexOf(' '));
                    let sigParams = signature.substring(signature.indexOf(' func') + 5);
                    let searchSignature = 'func ' + sigName + sigParams;
                    for (let i = 0; i < godocLines.length; i++) {
                        if (godocLines[i] === searchSignature) {
                            while (godocLines[++i].startsWith('    ')) {
                                doc += godocLines[i].substring(4) + '\n';
                            }
                            break;
                        }
                    }
                    if (doc !== '') {
                        definitionInformation.doc = doc;
                    }
                    return resolve(definitionInformation);
                });
            } catch (e) {
                console.log('e', e);
                reject(e);
            }
        });
        p.stdin.end(document.getText());
    });
}

function definitionLocation_gogetdoc(document, position, offset: number, env: any, useTags: boolean): Promise<GoDefinitionInformation> {
    let gogetdoc = getBinPath('gogetdoc');
    if (!path.isAbsolute(gogetdoc)) {
        return Promise.reject(missingToolMsg + 'gogetdoc');
    }
    let p: cp.ChildProcess;

    return new Promise<GoDefinitionInformation>((resolve, reject) => {

        let gogetdocFlagsWithoutTags = ['-u', '-json', '-modified', '-pos', document.fileName + ':#' + offset.toString()];
        let gogetdocFlags = gogetdocFlagsWithoutTags;
        p = cp.execFile(gogetdoc, gogetdocFlags, { env }, (err, stdout, stderr) => {
            try {
                if (err && (<any>err).code === 'ENOENT') {
                    return reject(missingToolMsg + 'gogetdoc');
                }
                if (stderr && stderr.startsWith('flag provided but not defined: -tags')) {
                    p = null;
                    return definitionLocation_gogetdoc(document, position, offset, env, false);
                }
                if (err) {
                    return reject(err.message || stderr);
                }
                let goGetDocOutput = <GoGetDocOuput>JSON.parse(stdout.toString());
                let match = /(.*):(\d+):(\d+)/.exec(goGetDocOutput.pos);
                let definitionInfo = {
                    file: null,
                    line: 0,
                    column: 0,
                    toolUsed: 'gogetdoc',
                    declarationlines: goGetDocOutput.decl.split('\n'),
                    doc: goGetDocOutput.doc,
                    name: goGetDocOutput.name,
                };
                if (!match) {
                    return resolve(definitionInfo);
                }
                let [_, file, line, col] = match;
                definitionInfo.file = match[1];
                definitionInfo.line = +match[2] - 1;
                definitionInfo.column = +match[3] - 1;
                return resolve(definitionInfo);

            } catch (e) {
                reject(e);
            }
        });
        p.stdin.end(getFileArchive(document));
    });
}

export class GoDefinitionProvider {
    constructor() {
    }

    public provideDefinition(document, position): Thenable<any> {
        return definitionLocation(document, position, false).then(definitionInfo => {
            if (definitionInfo == null || definitionInfo.file == null) {return null; }
            let definitionResource = Uri.file(definitionInfo.file);
            let pos = new Position(definitionInfo.line, definitionInfo.column);
            return new Location(definitionResource, pos);
        }, err => {
            if (err) {
                // Prompt for missing tool is located here so that the
                // prompts dont show up on hover or signature help
                console.log('err', err);
                return Promise.reject(err);
            }
            return Promise.resolve(null);
        });
    }
}

interface GoGetDocOuput {
    name: string;
    import: string;
    decl: string;
    doc: string;
    pos: string;
}

interface GuruDefinitionOuput {
    objpos: string;
    desc: string;
}
